import React from "react";
import { Component } from "react";
import { Form, InputGroup, Button } from 'react-bootstrap';
import { enumAggregations, numberAggregations, stringAggregations } from "../../chartUtils";
import { Definition, Entity, Property } from '../../data/Definition';
import { IItem, IMeasure } from "../../data/Query";
import { isEntity, isProperty, isStats } from "../../data/utils";

interface IMeasureCtrlProps {
    title: string;
    definition: Definition;
    measure: IMeasure | null;
    propertyTypes: string[];
    includeRoot: boolean;
    onUpdate: Function;
}

interface IMeasureCtrlState {
    showConfig : boolean,
    isConfigValid: boolean,
    isEditing: Boolean,
    config: IMeasure,
    current: IMeasure | null
};

class MeasureCtrl extends Component<IMeasureCtrlProps, IMeasureCtrlState>  {
    private options: IItem[];

    static defaultProps = {
        title: 'Measure (value to show)',
        entities: [],
        propertyTypes: ['string', 'number', 'property_lookup', 'stats'],
        includeRoot: true
      }

    constructor(props: any) {
        super(props);

        this.state = {
            showConfig: false,
            isConfigValid: false,
            isEditing: false,
            config: {} as IMeasure,                  
            current: this.props.measure
        };

        this.options = [];
    }

    componentDidMount() {
        this.initPropertyLists();
    }

    componentDidUpdate(prevProps:any){
        if(prevProps.measure !== this.props.measure){
            this.setState({          
                current: this.props.measure
            });
        }
    }

    initPropertyLists() {
        if(this.props.includeRoot) {
            this.options.push({
                item: this.props.definition as Entity, // main definition enitiy
                sub: null
            });
        }
        
        this.props.definition.getPropertiesOfType(this.props.propertyTypes).forEach((property: Property) => {
            this.options.push({
                item: property,
                sub: null
            });
        });
    }

    render() {
        return (
            <Form.Group controlId="yMeasure">
                <Form.Label>{this.props.title}</Form.Label>
                { this.renderCurrent() }
                { this.renderEdit() }     
            </Form.Group>
        );
    }

    renderCurrent() {
        const {current} = this.state;

        return(
            this.state.showConfig ? 
                '' :
                <InputGroup className="mb-3">
                    <Form.Control 
                        type="text" 
                        value={current && current.source.item ? this.getMeasureSummary(current) : ''} 
                        readOnly
                        placeholder="add a measure"
                        onClick={this.onShowConfig.bind(this)}
                    />

                    { current && current.source.item != null ?
                        <InputGroup.Append>
                            <Button variant="outline-secondary" onClick={this.onClear.bind(this)}>X</Button>
                        </InputGroup.Append>
                        : ''
                    }
                </InputGroup>
        );
    }

    renderEdit() {
        const {config} = this.state;

        return (
            this.state.showConfig ?
                <div style={{backgroundColor: "silver", padding: "7px"}}>
                    <Form.Group>
                        <Form.Label>Entity or Property</Form.Label>
                        <Form.Control as="select" name="yMeasure"
                            onChange={this.onItemChange.bind(this)}
                            defaultValue={config.source.item ? this.options.map((e:IItem) => { return e.item!.name; }).indexOf(config.source.item.name) : "-1"}>
                            <option value="-1">Select measure</option>
                            {this.options.map((item: any, i: number) => (
                                <option key={i} value={i}>{this.getItemDescription(item)}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    {
                        // value.
                        config.source.item && isStats(config.source.item) ?
                        <Form.Group>
                            <Form.Label>Statistics Property Value</Form.Label>
                            <Form.Control as="select" className="mt-1"
                                onChange={this.onValueChange.bind(this)}
                                value={config.source.sub ? config.source.sub : "-1"}>
                                <option value="-1">Select value</option>
                                {this.getValueOptionsForType(config.source).map((option: string, i: number) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        :''
                    }

                    {
                        // aggregation.
                        config.source.item ?
                        <Form.Group controlId="yMeasureBin">
                            <Form.Label>Aggregation</Form.Label>
                            <Form.Control as="select" name="yMeasureBin" className="mt-1"
                                onChange={this.onAggregationChange.bind(this)}
                                value={config.aggregation ? this.state.config.aggregation : "-1"}>
                                <option value="-1">Select aggregation</option>
                                {this.getAggregationForType(config.source).map((item: any, i: number) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        :''
                    }
                    
                    <div className="mt-1">
                        <Button variant="warning" disabled={!this.state.isConfigValid} onClick={this.onSave.bind(this)}>Save</Button>
                        <Button variant="link" onClick={this.onCancel.bind(this)}>cancel</Button>
                    </div>
                </div>
            : ''
        );
    }

    isConfigValid(measure: IMeasure | null) {
        const isItemValid = measure != null && measure.source.item != null;
        let isSubItemValid = true;

        if(measure != null && measure.source.item != null && isStats(measure.source.item)) {
            isSubItemValid = measure.source.sub != null;
        }

        const isAggregationValid = measure !== null && measure.aggregation != undefined;

        return isItemValid && isSubItemValid && isAggregationValid;
    }

    getValueOptionsForType(item: IItem) {
        if(isStats(item.item)) {
            const property = item.item as Property;
            return property.stats;
        }

        return [];
    }

    onItemChange(e: any) {
        const measure: IMeasure = {
            source: {
                item: e.target.value === "-1" ? null : this.options[e.target.value].item,
                sub: null
            },
            aggregation: undefined
        };

        this.setState({
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onValueChange(e: any) {
        const measure: IMeasure = {
            source: {
                item: this.state.config.source.item,
                sub: e.target.value
            },
            aggregation: this.state.config.aggregation
        };

        this.setState({
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onAggregationChange(e: any) {
        const measure: IMeasure = {
            source: {
                item: this.state.config.source.item,
                sub: this.state.config.source.sub
            },
            aggregation: e.target.value
        };

        this.setState({
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onClear(e: any) {
        this.setState({
            current: null
        },() => {
            this.props.onUpdate(this.state.current);
        });
    }

    onShowConfig(e: any) {  
        // copy state to config  
        const measure: IMeasure = this.state.current ? { ...this.state.current, source: {...this.state.current.source}} : { source: { item: null, sub: null }, aggregation: undefined};
    
        this.setState({
            showConfig: true,
            isConfigValid: this.isConfigValid(measure),
            config: measure
        });
    }

    onCancel(e: any) {
        this.setState({
            showConfig: false,
            config: {
                source: {
                    item: null,
                    sub: null
                },
                aggregation: undefined
            }
        });
    } 

    onSave(e: any) {
        const measure: IMeasure = this.state.config ? { ...this.state.config, source: {...this.state.config.source}} : { source: { item: null, sub: null }, aggregation: undefined};
        if(measure.source.sub == null) {
            measure.source.sub = 'value';
        }

        this.setState({
            showConfig: false,
            current: measure
        }, () => {
            this.props.onUpdate(this.state.current);
        });
    }

    getAggregationForType(item: IItem) {
        if(isEntity(item.item)) {
            return ['count'];
        } else if(isProperty(item.item)) {
            const property = item.item as Property;
            if(property.type === 'string') {
                return stringAggregations;
            } else if(['number', 'stats'].includes(property.type)) {
                return numberAggregations;
            } else if(property.type === 'property_lookup') {
                return enumAggregations;
            }
        }

        return [];
    }

    getMeasureSummary(measure: IMeasure): string {
        let itemStr = this.getItemDescription(measure.source);
        let binStr = measure.aggregation ? `agg:${measure.aggregation}` : '';
        return `${itemStr} ${binStr}`;
    }

    getItemDescription(item: IItem): string {
        if(item != null && item.item != null) {
            if(isEntity(item.item)) {
                const entity = item.item as Entity;
                return `entity: ${entity.name}`;
            } else if(isProperty(item.item)) {
                const property = item.item as Property;
                const subItemStr = item.sub != null ? `[${item.sub}]` : '';
                return `property (${property.type}): ${property.name}${subItemStr}`;
            }
        }

        return 'unknown';
    }
}

export default MeasureCtrl;